import type { Body, Catalogue } from "../data/schema";
import { bodyName, fill, messages, type Lang } from "../i18n/index";

/** Moons listed under an expanded body before the list points to search instead. */
export const MOON_GROUP_CAP = 20;

const KIND_ORDER: Record<Body["kind"], number> = { star: 0, planet: 1, dwarf: 2, moon: 3 };
export function kindLabel(kind: Body["kind"], lang: Lang): string {
  return messages(lang).kinds[kind];
}

/** A body's parent as shown: its id and its name in the current language. */
export interface ParentRef {
  id: string;
  name: string;
}

/** "moon of Jupiter", "planet of the Sun", or just the kind for the root. */
export function bodySubtitle(kind: Body["kind"], parent: ParentRef | null, lang: Lang): string {
  const m = messages(lang);
  if (parent === null) return m.kinds[kind];
  const template = parent.id === "sun" ? m.subtitle.ofSun : m.subtitle.of;
  return fill(template, { kind: m.kinds[kind], parent: parent.name });
}

/** The catalogue with each name in the language, for the list and the search. */
export function localizeNames(bodies: Catalogue, lang: Lang): Catalogue {
  return bodies.map((b) => {
    const name = bodyName(lang, b);
    return name === b.name ? b : { ...b, name };
  });
}

export interface TreeNode {
  id: string;
  name: string;
  kind: Body["kind"];
  color: string;
  children: TreeNode[];
}

const semiMajorAxis = (b: Body): number => b.orbit?.a ?? 0;

/** Sun, planets and dwarfs at the top level by distance; moons nested under their parent. */
export function buildBodyTree(bodies: Catalogue): TreeNode[] {
  const nodes = new Map<string, TreeNode>();
  for (const b of bodies)
    nodes.set(b.id, {
      id: b.id,
      name: b.name,
      kind: b.kind,
      color: b.physical.color,
      children: [],
    });
  const sorted = [...bodies].sort(
    (x, y) => KIND_ORDER[x.kind] - KIND_ORDER[y.kind] || semiMajorAxis(x) - semiMajorAxis(y),
  );
  const roots: TreeNode[] = [];
  for (const b of sorted) {
    const node = nodes.get(b.id)!;
    const parent = b.kind === "moon" && b.parent !== null ? nodes.get(b.parent) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

export function splitGroup(
  node: TreeNode,
  cap = MOON_GROUP_CAP,
): { shown: TreeNode[]; more: number } {
  return { shown: node.children.slice(0, cap), more: Math.max(0, node.children.length - cap) };
}

/** The tree group that holds a body: its parent for a moon, the body itself otherwise. */
export function groupFor(bodies: Catalogue, id: string): string {
  const body = bodies.find((b) => b.id === id);
  return body?.kind === "moon" && body.parent !== null ? body.parent : id;
}

export interface SearchHit {
  id: string;
  name: string;
  kind: Body["kind"];
  color: string;
  parent: ParentRef | null;
  /** Match position in `name`, or -1 when normalisation changed the length. */
  start: number;
  length: number;
}

export function normalizeName(name: string): string {
  return name.normalize("NFD").replace(/\p{M}/gu, "").replace(/_/g, " ").toLowerCase();
}

/** Name search: prefix matches first, then substring matches, larger bodies first. */
export function searchBodies(bodies: Catalogue, query: string, limit = 50): SearchHit[] {
  const q = normalizeName(query.trim());
  if (q === "") return [];
  const names = new Map(bodies.map((b) => [b.id, b.name]));
  const scored: { hit: SearchHit; rank: number; radius: number }[] = [];
  for (const b of bodies) {
    const normalized = normalizeName(b.name);
    const at = normalized.indexOf(q);
    if (at < 0) continue;
    scored.push({
      hit: {
        id: b.id,
        name: b.name,
        kind: b.kind,
        color: b.physical.color,
        parent: b.parent === null ? null : { id: b.parent, name: names.get(b.parent) ?? b.parent },
        start: normalized.length === b.name.length ? at : -1,
        length: q.length,
      },
      rank: at === 0 ? 0 : 1,
      radius: b.physical.radiusKm,
    });
  }
  scored.sort(
    (x, y) => x.rank - y.rank || y.radius - x.radius || x.hit.name.localeCompare(y.hit.name),
  );
  return scored.slice(0, limit).map((s) => s.hit);
}
